/* eslint-disable @typescript-eslint/no-explicit-any */
import { format, isValid } from 'date-fns';
import { AnyObject } from 'types';

type AcceptableDate = string | Date;

function toDate(date: AcceptableDate): Date {
  return typeof date === 'string' ? new Date(date) : date;
}

export const getTimezoneOffset = (): number => new Date().getTimezoneOffset();

export function getDateTime(timestamp: string): string {
  const date = new Date(timestamp);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

const dateFormats = {
  'dd/mm/yyyy': "dd'/'MM'/'yyyy",
  'yyyy-mm-dd': "yyyy'-'MM'-'dd",
  // PPP = date written version => https://date-fns.org/v4.1.0/docs/format check for more
} as const;

type DateFormat = keyof typeof dateFormats;

/**
 * Formats dates to specific formats
 *
 * IMPORTANT: Be aware of TZ conversions
 *
 * ===> new Date('2024-10-11') => On UTC-3 (SP timezone) results in 2024-10-10 21:00hrs
 *
 * ===> Which would then be converted with the format. CAREFUL!!
 */
export function formatDate(date: AcceptableDate, pattern: DateFormat): string {
  return format(toDate(date), dateFormats[pattern]);
}

const fromYYYYmmDD = (s: string) => new Date(`${s}T00:00:00.000`);

const formatRevivers: Record<DateFormat, (s: string) => Date> = {
  'yyyy-mm-dd': fromYYYYmmDD,

  'dd/mm/yyyy': (date) => fromYYYYmmDD(date.split('/').reverse().join('-')),
};

/**
 * Revives a date string correctly aware of the user TZ
 *
 * ===> new Date('2024-10-11') => On UTC-3 (SP timezone) results in 2024-10-10 21:00hrs
 *
 * ===> dateFromFormat('2024-10-11', 'yyyy-mm-dd') => Date obj on correct date
 */
export function dateFrom(target: string, format: DateFormat) {
  const date = formatRevivers[format](target);

  if (!isValid(date)) throw Error(`Bad date format ${target} format:${format}`);

  return date;
}

/**
 *
 * @param dateString The date to validate is in the yyyy-mm-dd format
 * @returns true/false
 */
export function isValidYYYYmmDD(date: string) {
  try {
    dateFrom(date, 'yyyy-mm-dd');

    return true;
  } catch (err) {
    return false;
  }
}

/**
 *
 * @param dateString The date to validate
 * @returns true/false
 */
export function isValidDateFormat(date: string, format: DateFormat) {
  try {
    dateFrom(date, format);

    return true;
  } catch (err) {
    return false;
  }
}

/**
 *
 * @param values An object containing one or more yyyy-mm-dd date strings
 * @param target The keys to revive and return as dates
 * @returns An object that contains the successfully revived dates
 */
export function reviveDates<T extends Record<string, string>, Target extends keyof T = keyof T>(
  values: T,
  target?: Target[],
): { [Key in Target]: Date } {
  const actualTarget = target ?? (Object.keys(values) as Target[]);

  const revived = Object.fromEntries(
    actualTarget
      .filter((key) => isValidYYYYmmDD(values[key]))
      .map((key) => [key, dateFrom(values[key], 'yyyy-mm-dd')]),
  );

  return revived as { [Key in Target]: Date };
}

type DateKeys<T extends AnyObject> = NonNullable<
  { [Key in keyof T]: T[Key] extends Date ? Key : null }[keyof T]
>;

/**
 *
 * @param values An object containing one or more dates
 * @param target The keys to revive and return as YYYY-MM-DD dates
 * @returns An object that contains the successfully serialized dates
 */
export function serializeDates<T extends AnyObject, Target extends DateKeys<T> = DateKeys<T>>(
  values: T,
  target?: Target[],
): { [Key in Target]: string } {
  const actualTarget = target ?? (Object.keys(values) as Target[]);

  const revived = Object.fromEntries(
    actualTarget
      .filter((key) => (values[key] as any) instanceof Date)
      .map((key) => [key, formatDate(values[key], 'yyyy-mm-dd')]),
  );

  return revived as { [Key in Target]: string };
}

export function getTimestamp() {
  return new Date().toISOString();
}
